/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import Modal from '@pedidosya/web-fenix/system/Modal';
import Button from '@pedidosya/web-fenix/system/Button';
import SimpleButton from '@components/SimpleButton';
import { ROUTES } from '@services/routes.service';
import { injectIntl, intlShape } from 'react-intl';
import useIsDesktopLayout from '@hooks/useIsDesktopLayout';
import { MODAL_TYPE, TRACKING_ORIGIN } from '@commons/constants';
import QRCode from '@shared/components/QRCode/QRCode';

import messages from '../messages';
import goToAppLink from '../getNativeAppLink';
function CartFooter({
  editMode,
  needsToAdd,
  onConfirmProductsDeletion,
  trackCheckoutClicked,
  trackModalOpened,
  trackModalClosed,
  showPhoneVerificationModal,
  intl: { formatMessage },
  isUpdatingLines,
  shopId,
  cartId,
  searchedLocation,
  showNativeModal,
  isNewUser,
  countryShortName,
}) {
  const [showModal, setShowModal] = useState(null);
  const [showModalQR, setShowModalQR] = useState(null);
  const [modalCloseOnAction, setModalCloseOnAction] = useState('');

  const isDesktopLayout = useIsDesktopLayout();

  const labelFooter = messages.cart_footer_continue;

  const handleOnClickTrackCheckout = event => {
    if (isUpdatingLines) {
      event.preventDefault();
    } else {
      trackCheckoutClicked();
    }
  };

  const handleOnModalClose = () => {
    if (modalCloseOnAction !== TRACKING_ORIGIN.BUTTON.DOWNLOAD) {
      setModalCloseOnAction(TRACKING_ORIGIN.BUTTON.CLOSE);
    }
    if (trackModalClosed) {
      trackModalClosed(
        MODAL_TYPE.INSTALL_APP,
        TRACKING_ORIGIN.BUTTON.CLOSE,
        TRACKING_ORIGIN.BUTTON.TYPE,
      );
    }
    setShowModal(false);
  };

  const handleOnDowloadClick = () => {
    goToAppLink({
      cartId,
      shopId,
      searchedLocation,
    });
    setModalCloseOnAction(TRACKING_ORIGIN.BUTTON.DOWNLOAD);
    setShowModal(false);
  };

  const getQrDeeplink = () =>
    `${window.location.origin}${ROUTES.CHECKOUT}?origin=qr_cart___cartId=${cartId}___shopId=${shopId}___lat=${searchedLocation?.latitude}___long=${searchedLocation?.longitude}&c=${countryShortName}`;

  const handleOnGotoPayClick = e => {
    if (showNativeModal && isDesktopLayout && isNewUser) {
      e.preventDefault();
      setShowModalQR(true);
    } else {
      handleOnClickTrackCheckout();
    }
  };

  useEffect(() => {
    if (trackModalOpened && showNativeModal && (showModal || showModalQR)) {
      trackModalOpened(MODAL_TYPE.INSTALL_APP);
    }
  }, [showNativeModal, showModal, showModalQR, trackModalOpened]);

  const handleOnQRModalClose = () => {
    setShowModalQR(false);
    if (trackModalClosed) {
      trackModalClosed(
        MODAL_TYPE.INSTALL_APP,
        TRACKING_ORIGIN.BUTTON.CLOSE,
        TRACKING_ORIGIN.BUTTON.TYPE,
      );
    }
  };

  if (showNativeModal && !isDesktopLayout && isNewUser) {
    return (
      <>
        <SimpleButton
          elementId="cart__continue__action"
          label={formatMessage(labelFooter)}
          color="primary"
          disabled={isUpdatingLines}
          onClick={() => setShowModal(true)}
        />
        <Modal
          open={!!showModal}
          onClose={handleOnModalClose}
          primaryButton={
            <Button
              fullWidth
              label={formatMessage(messages.qr_modal_continue_button_label)}
              size="large"
              onClick={handleOnDowloadClick}
            />
          }
          title={formatMessage(messages.qr_mobile_modal_title)}
          text={formatMessage(messages.qr_mobile_modal_text)}
        />
      </>
    );
  }

  if (!editMode.on && needsToAdd === 0) {
    const pathToPush = showPhoneVerificationModal
      ? ROUTES.CART_PHONE_VALIDATION
      : ROUTES.CHECKOUT;

    return (
      <>
        <NavLink to={pathToPush} onClick={handleOnGotoPayClick}>
          <SimpleButton
            elementId="cart__continue__action"
            label={formatMessage(labelFooter)}
            color="primary"
            disabled={isUpdatingLines}
          />
        </NavLink>
        <Modal
          open={!!showModalQR}
          onClose={handleOnQRModalClose}
          primaryButton={
            <Button
              fullWidth
              label={formatMessage(messages.qr_modal_continue_button_label)}
              size="large"
              onClick={handleOnQRModalClose}
            />
          }
          title={formatMessage(messages.qr_web_modal_title)}
          text={formatMessage(messages.qr_web_modal_text)}
        >
          <QRCode link={getQrDeeplink()} />
        </Modal>
      </>
    );
  }

  if (editMode.on) {
    return (
      <SimpleButton
        elementId="cart__delete__product__action"
        label={
          editMode.itemsToRemove?.length > 0
            ? formatMessage(messages.cart_footer_remove)
            : formatMessage(labelFooter)
        }
        color="primary"
        onClick={onConfirmProductsDeletion}
        disabled={editMode.isDeletingLines}
      />
    );
  }

  return null;
}

CartFooter.propTypes = {
  editMode: PropTypes.shape({
    on: PropTypes.bool,
    itemsToRemove: PropTypes.array,
    isDeletingLines: PropTypes.bool,
  }).isRequired,
  isUpdatingLines: PropTypes.bool,
  needsToAdd: PropTypes.number.isRequired,
  trackCheckoutClicked: PropTypes.func.isRequired,
  trackModalOpened: PropTypes.func.isRequired,
  trackModalClosed: PropTypes.func.isRequired,
  onConfirmProductsDeletion: PropTypes.func.isRequired,
  showPhoneVerificationModal: PropTypes.bool.isRequired,
  shopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  intl: intlShape.isRequired,
  searchedLocation: PropTypes.object,
  showNativeModal: PropTypes.bool,
  isNewUser: PropTypes.bool,
  countryShortName: PropTypes.string,
};

export default injectIntl(CartFooter);
